import { getEventsByDomainUUID, parseEventCards } from '@/libraries/api/events'
import { Service, Operation } from '@/libraries/permissions'
import { parseEventsByDay, filterEvents, deduplicateEvents } from '@/libraries/util/events'

/**
 * @function updateFilters
 * @param
 * @param data {object}: object key sets the filter category, while the value the filter itself. For example const data = {detectable: 'pet'}
 */
export const EventsModule = {
  namespaced: true,
  state: {
    events: [],
    filters: {
      cameras: [],
      detectables: [],
      cameraTags: [],
    },
    queryParameters: {
      limit: 500,
      start: undefined,
      end: undefined,
      paginationToken: undefined,
    },
    searchString: undefined,
    loading: undefined,
  },
  mutations: {
    updateFilters (state, data) {
      let updatedFilters = state.filters[data.category]
      if (data.add) {
        updatedFilters.push(data.filter)
      } else {
        updatedFilters = state.filters[data.category].filter(filter => {
          return (filter !== data.filter)
        })
        state.filters[data.category] = updatedFilters
      }
    },
    clearFilters (state) {
      state.filters = {
        cameras: [],
        detectables: [],
        cameraTags: [],
      }
    },
    clearEvents (state) {
      state.queryParameters.paginationToken = null
      state.events = []
    },
    prependEvents (state, events) {
      state.events = deduplicateEvents(events.concat(state.events))
    },
    appendEvents (state, events) {
      state.events = deduplicateEvents(state.events.concat(events))
    },
    updateSearchTerm (state, searchString) {
      state.searchString = searchString
    },
    setPaginationToken (state, timestamp) {
      state.queryParameters.paginationToken = timestamp
    },
    setQueryReturnLimit (state, limit) {
      state.queryParameters.limit = limit
    },
    setQueryStartDate (state, timestamp) {
      state.queryParameters.start = timestamp
    },
    setQueryEndDate (state, timestamp) {
      state.queryParameters.end = timestamp
    },
    setLoading (state, loading) {
      state.loading = loading
    },
  },
  getters: {
    events (state) {
      return state.events
    },
    loading (state) {
      return state.loading
    },
    cameraTags(state) {
      return state.events.map(event => ({
        cameraId: event.cameraId,
        cameraDescription: event.cameraDescription,
        cameraTags: event.cameraTags ? Object.keys(event.cameraTags) : [],
      }))
    },
    uniqueCameras(state) {
      const seen = new Set()
      return state.events.reduce((acc, event) => {
        if (!seen.has(event.cameraId)) {
          seen.add(event.cameraId)
          acc.push({ cameraId: event.cameraId, cameraDescription: event.cameraDescription })
        }
        return acc
      }, [])
    },
    detectablesByType (state) {
      const detectionsDict = {}
      state.events.map(event => {
        event.metrics.map(detectable => {
          // detectable is {detectable: count} e.g. {car:1}
          const description = Object.keys(detectable)[0]
          if (description in detectionsDict) {
            detectionsDict[description] += detectable[description]
          } else {
            detectionsDict[description] = detectable[description]
          }
        })
      })
      let sortable = []

      for (var metric in detectionsDict) {
        sortable.push([metric, detectionsDict[metric]])
      }

      sortable.sort((a, b) => {
        return b[1] - a[1]
      })
      const detections = []
      sortable.map(result => {
        if (result[1] !== 0) {
          detections.push({
            description: result[0].replace('ssd_', ''),
            detections: result[1],
          })
        }
      })

      return detections
    },
    filters (state) {
      return state.filters
    },
    queryParameters (state) {
      return state.queryParameters
    },
    paginationToken (state) {
      return state.paginationToken
    },
    hasVisibleEvents (state) {
      return filterEvents(state.events, state.filters).length > 0
    },
    eventsByDay (state) {
      const filteredEvents = filterEvents(state.events, state.filters)
      const byDay = parseEventsByDay(filteredEvents)

      return filteredEvents.length > 0 ? byDay : undefined
    },
    detectableTypesList (state) {
      let types = [...new Set(state.events.map((o) => o.className))]
      let toRemove = types.filter(s => s.includes(','))
      toRemove.push('')
      let listOfTypes = types.filter((el) => !toRemove.includes(el))
      let descriptionOfTypes = listOfTypes.map(x => {
        return { description: x, checkboxValue: false }
      })
      descriptionOfTypes.push({ description: 'all', checkboxValue: false })
      return descriptionOfTypes
    },
  },
  actions: {
    loadEventsForDomain ({ commit, rootGetters, rootState, state }, params) {
      console.log('loadEventsForDomain called with parapms', params)
      const canPerformOperationForService = rootGetters['entitlements/canPerformOperationForService']
      const canAccessEvents = canPerformOperationForService(Service.ALERTING, Operation.VIEW_HISTORIC)

      if (!canAccessEvents) {
        console.log('couldnt access events')
        return new Promise((resolve, reject) => reject(Error(`Entitlements Error: Can't access alerts.`)))
      }

      commit('setLoading', true)

      const query = { limit: 250 }

      if (state.queryParameters.paginationToken) {
        query.pagination_token = state.queryParameters.paginationToken
      }

      Object.assign(query, params)
      return getEventsByDomainUUID(rootState.data.domains.current.uuid, query)
        .then(response => {
          console.log(response.data)
          return response.data
        })
        .then(res => {
          const parsedEvents = parseEventCards(res.data)
          commit('setPaginationToken', res.paginationToken)
          commit('appendEvents', parsedEvents)
          commit('setLoading', false)
        }).catch(error => {
          console.error(error)
          commit('setLoading', false)
        })
    },
    addEvent ({ commit }, event) {
      return new Promise((resolve, reject) => {
        const events = parseEventCards([event])
        commit('prependEvents', events)
      })
    },
    clearEventsData ({ state, commit }) {
      commit('clearEvents')
    },
  },
}
